import React, { useState } from 'react';
import { Copy, CheckCircle, Mail, Tag, Paperclip, Calendar, User } from 'lucide-react';
import { generateProjectEmailUrl } from '../utils/urlManager';
import { Modal, ModalBody, ModalFooter, Button, Badge } from './ui';

const ProjectEmailModal = ({ email, isOpen, onClose }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  if (!isOpen || !email) return null;

  const handleCopyLink = async () => {
    try {
      const link = `${window.location.origin}${generateProjectEmailUrl(email.id)}`;
      await navigator.clipboard.writeText(link);
      setCopySuccess(true);
      
      // Reset copy success after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const formatJobNumber = () => {
    if (!email?.projects?.job_number) return "N/A";
    const jobWithRev = email.projects.rev
      ? `${email.projects.job_number} Rev ${email.projects.rev}`
      : email.projects.job_number;
    return jobWithRev;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAddresses = (addresses) => {
    if (!addresses || !Array.isArray(addresses)) return 'N/A';
    return addresses.join(', ');
  };

  return (
    <Modal trackAs="ProjectEmailModal" isOpen={isOpen} onClose={onClose} size="lg">
      {/* Custom Header */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Mail size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {email.projects ? `${formatJobNumber()} - ${email.projects.name}` : 'Unassigned Project Email'}
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {email.subject || 'No Subject'}
            </p>
          </div>
        </div>

        {/* Email Type and Attachments Info */}
        {(email.email_type || (email.attachment_urls && email.attachment_urls.length > 0)) && (
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)', flexWrap: 'wrap' }}>
            {email.email_type && (
              <Badge variant="info" size="sm">
                <Tag size={12} style={{ marginRight: '4px' }} />
                {email.email_type}
              </Badge>
            )}
            {email.attachment_urls && email.attachment_urls.length > 0 && (
              <Badge variant="neutral" size="sm">
                <Paperclip size={12} style={{ marginRight: '4px' }} />
                {email.attachment_urls.length} attachment{email.attachment_urls.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        )}
      </div>

      <ModalBody>
        {/* Summary Section */}
        {email.summary && (
          <div
            style={{
              padding: 'var(--space-4)',
              marginBottom: 'var(--space-6)',
              backgroundColor: 'var(--dark-elevated)',
              border: '1px solid var(--gray-600)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-2)',
                color: 'var(--gray-400)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-medium)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Summary
            </label>
            <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
              {email.summary}
            </div>
          </div>
        )}

        {/* Email Metadata */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h2
            style={{
              margin: 0,
              marginBottom: 'var(--space-4)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
            }}
          >
            Email Details
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)' }}>
            {/* From */}
            <div
              style={{
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-2)',
                  color: 'var(--gray-400)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-medium)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                <User size={12} />
                From
              </label>
              <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)' }}>
                {email.from_address || 'N/A'}
              </div>
            </div>

            {/* To */}
            <div
              style={{
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-2)',
                  color: 'var(--gray-400)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-medium)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                <User size={12} />
                To
              </label>
              <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)' }}>
                {formatAddresses(email.to_addresses)}
              </div>
            </div>

            {/* CC */}
            {email.cc_addresses && email.cc_addresses.length > 0 && (
              <div
                style={{
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-lg)',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  <User size={12} />
                  CC
                </label>
                <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)' }}>
                  {formatAddresses(email.cc_addresses)}
                </div>
              </div>
            )}

            {/* Date */}
            <div
              style={{
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-2)',
                  color: 'var(--gray-400)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-medium)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                <Calendar size={12} />
                Received
              </label>
              <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)' }}>
                {formatDate(email.received_at)}
              </div>
            </div>
          </div>
        </div>

        {/* Email Body */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h2
            style={{
              margin: 0,
              marginBottom: 'var(--space-4)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
            }}
          >
            Email Content
          </h2>

          <div
            style={{
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-surface)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-lg)',
              minHeight: '200px',
              maxHeight: '600px',
              overflow: 'auto',
            }}
          >
            {email.body_html ? (
              <iframe
                srcDoc={email.body_html}
                style={{
                  width: '100%',
                  minHeight: '400px',
                  border: 'none',
                  backgroundColor: 'white',
                  borderRadius: 'var(--radius-md)',
                }}
                title="Email Content"
                sandbox="allow-same-origin"
              />
            ) : email.body_text ? (
              <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap', lineHeight: 'var(--leading-relaxed)' }}>
                {email.body_text}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-8)',
                  color: 'var(--gray-500)',
                }}
              >
                <Mail size={32} style={{ marginBottom: 'var(--space-4)' }} />
                <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>No email content available</p>
              </div>
            )}
          </div>
        </div>

        {/* Attachments */}
        {email.attachment_urls && email.attachment_urls.length > 0 && (
          <div>
            <h2
              style={{
                margin: 0,
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
              }}
            >
              Attachments
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
              {email.attachment_urls.map((url, index) => (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                    textDecoration: 'none',
                    color: 'var(--gray-300)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                    e.currentTarget.style.borderColor = 'var(--cta-blue)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dark-surface)';
                    e.currentTarget.style.borderColor = 'var(--gray-700)';
                  }}
                >
                  <div
                    style={{
                      flexShrink: 0,
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'var(--dark-elevated)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <Paperclip size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {url.split('/').pop() || `Attachment ${index + 1}`}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Button
            variant="ghost"
            size="md"
            onClick={handleCopyLink}
            icon={copySuccess ? <CheckCircle size={16} /> : <Copy size={16} />}
          >
            {copySuccess ? 'Copied!' : 'Copy Link'}
          </Button>

          <Button variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ProjectEmailModal;